import { zuSchiebern } from './farbraeder.js';
const prozent = (v) => `${Math.round(v * 100)} %`;
function farbe(clip) {
    return clip.effects.find((e) => e.type === 'colorAdjust');
}
function lut(clip) {
    return clip.effects.find((e) => e.type === 'lut');
}
function raeder(clip) {
    return clip.effects.find((e) => e.type === 'farbraeder');
}
/**
 * Ein Bereich der Farbräder in Worten (S14).
 *
 * Nicht drei Zahlen, sondern das, was an den zwei Schiebern steht -- sonst
 * müsste man im Kopf zurückrechnen, was `0,15 / -0,30 / -0,15` bedeutet.
 */
function radText(werte) {
    const { waerme, gruenMagenta } = zuSchiebern(werte);
    const teile = [];
    if (!gleich(waerme, 0))
        teile.push(`${waerme > 0 ? 'wärmer' : 'kühler'} ${prozent(Math.abs(waerme))}`);
    if (!gleich(gruenMagenta, 0))
        teile.push(`${gruenMagenta > 0 ? 'magenta' : 'grün'} ${prozent(Math.abs(gruenMagenta))}`);
    return teile.join(', ');
}
/** Fast gleich zählt als gleich: ein Regler trifft 1,0 selten auf die Stelle. */
function gleich(a, b, luft = 0.005) {
    return Math.abs(a - b) < luft;
}
export function abweichungen(clip) {
    const raus = [];
    const sag = (gruppe, label, wert) => raus.push({ gruppe, label, wert });
    if (clip.speedBis !== null && clip.speedBis !== undefined) {
        // Eine Rampe (S6) ist eine Angabe, nicht zwei: „von 1,00× auf 2,00×“ sagt
        // in einer Zeile, was zwei Zeilen mit „Tempo“ und „Tempo am Ende“ erst im
        // Kopf des Lesers zusammensetzen würden.
        sag('wiedergabe', 'Tempo', `${clip.speed.toFixed(2)}× → ${clip.speedBis.toFixed(2)}×`);
    }
    else if (!gleich(clip.speed, 1)) {
        sag('wiedergabe', 'Tempo', `${clip.speed.toFixed(2)}×`);
    }
    if (clip.keyframes.length > 0) {
        sag('bewegung', 'Keyframes', `${clip.keyframes.length}`);
    }
    if (clip.transitionIn)
        sag('uebergang', 'Übergang', `${clip.transitionIn.duration.toFixed(1)} s`);
    const l = lut(clip);
    if (l)
        sag('look', 'Look', `${l.name}, ${prozent(l.intensity)}`);
    const f = farbe(clip);
    if (f) {
        if (!gleich(f.brightness, 0))
            sag('bild', 'Helligkeit', f.brightness.toFixed(2));
        if (!gleich(f.contrast, 1))
            sag('bild', 'Kontrast', prozent(f.contrast));
        if (!gleich(f.saturation, 1))
            sag('bild', 'Sättigung', prozent(f.saturation));
        if (!gleich(f.gamma, 1))
            sag('bild', 'Gamma', f.gamma.toFixed(2));
    }
    const r = raeder(clip);
    if (r) {
        for (const [label, werte] of [['Tiefen', r.tiefen], ['Mitten', r.mitten], ['Höhen', r.hoehen]]) {
            const text = radText(werte);
            if (text)
                sag('farbraeder', label, text);
        }
    }
    const t = clip.transform;
    if (!gleich(t.x, 0.5) || !gleich(t.y, 0.5))
        sag('ebene', 'Position', `${prozent(t.x)} / ${prozent(t.y)}`);
    if (!gleich(t.scale, 1))
        sag('ebene', 'Größe', prozent(t.scale));
    if (!gleich(t.rotate, 0))
        sag('ebene', 'Drehung', `${t.rotate.toFixed(0)}°`);
    if (!gleich(t.opacity, 1))
        sag('ebene', 'Deckkraft', prozent(t.opacity));
    if (!gleich(clip.gain, 1))
        sag('ton', 'Lautstärke', prozent(clip.gain));
    if (clip.muted)
        sag('ton', 'Stumm', 'ja');
    if (clip.gainKeyframes.length > 0)
        sag('ton', 'Pegelverlauf', `${clip.gainKeyframes.length} Punkte`);
    if (clip.fadeIn > 0)
        sag('ton', 'Einblenden', `${clip.fadeIn.toFixed(1)} s`);
    if (clip.fadeOut > 0)
        sag('ton', 'Ausblenden', `${clip.fadeOut.toFixed(1)} s`);
    return raus;
}
/** Wie viele je Gruppe -- die Zahl, die neben der Überschrift steht. */
export function abweichungenJeGruppe(clip) {
    const leer = {
        wiedergabe: [],
        bewegung: [],
        uebergang: [],
        look: [],
        bild: [],
        farbraeder: [],
        ebene: [],
        ton: [],
    };
    for (const a of abweichungen(clip))
        leer[a.gruppe].push(a);
    return leer;
}
/**
 * Was eine Gruppe auf Standard zurücksetzt.
 *
 * Gibt genau die Felder zurück, die sich ändern -- nicht den ganzen Clip.
 * Ein Rundumschlag würde beim Zurücksetzen der Farbe die Keyframes gleich
 * mitnehmen, und das hat niemand gemeint.
 */
export function zuruecksetzen(clip, gruppe) {
    switch (gruppe) {
        case 'wiedergabe':
            return { speed: 1, speedBis: null };
        case 'bewegung':
            return { keyframes: [] };
        case 'uebergang':
            return { transitionIn: null };
        case 'look':
            return { effects: clip.effects.filter((e) => e.type !== 'lut') };
        case 'bild':
            return { effects: clip.effects.filter((e) => e.type !== 'colorAdjust') };
        case 'farbraeder':
            return { effects: clip.effects.filter((e) => e.type !== 'farbraeder') };
        case 'ebene':
            return { transform: { x: 0.5, y: 0.5, scale: 1, rotate: 0, opacity: 1 } };
        case 'ton':
            return { gain: 1, muted: false, gainKeyframes: [], fadeIn: 0, fadeOut: 0 };
    }
}
export const BILD_VORLAGEN = [
    { name: 'kraeftiger', label: 'Kräftiger', wirkung: { brightness: 0, contrast: 1.12, saturation: 1.28, gamma: 1 } },
    { name: 'weicher', label: 'Weicher', wirkung: { brightness: 0.02, contrast: 0.9, saturation: 0.92, gamma: 1 } },
    { name: 'heller', label: 'Heller', wirkung: { brightness: 0.1, contrast: 1, saturation: 1, gamma: 1.06 } },
    { name: 'dunkler', label: 'Dunkler', wirkung: { brightness: -0.1, contrast: 1.05, saturation: 1, gamma: 0.94 } },
];
/** Welche Vorlage gerade steht -- oder null, wenn von Hand gestellt wurde. */
export function vorlageVon(clip) {
    const f = farbe(clip);
    if (!f)
        return null;
    const treffer = BILD_VORLAGEN.find((v) => gleich(v.wirkung.brightness, f.brightness) &&
        gleich(v.wirkung.contrast, f.contrast) &&
        gleich(v.wirkung.saturation, f.saturation) &&
        gleich(v.wirkung.gamma, f.gamma));
    return treffer?.name ?? null;
}
//# sourceMappingURL=einstellungen.js.map